import React, { useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Container, TextField, InputAdornment, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomButton from './common/CustomButton';
import SearchIcon from '@mui/icons-material/Search';
import sectorsData from '../data/sectors.json';
import { useNavigate } from 'react-router-dom';
import AnimatedUnderline from './common/AnimatedUnderline';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: '6rem 0',
  backgroundColor: '#F8FAFC',
  position: 'relative'
}));

const HeaderSection = styled(Box)({
  textAlign: 'center',
  marginBottom: '2rem'
});

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#004B87',
  marginBottom: '1.5rem',
  textAlign: 'center',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#475569',
  lineHeight: 1.7,
  textAlign: 'center',
  maxWidth: '800px',
  margin: '0 auto 2rem'
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginBottom: '2rem',
  alignItems: 'center',
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto'
}));

const SearchField = styled(TextField)({
  width: '100%',
  maxWidth: '500px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease',
    '&:hover': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#73c043'
      }
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#73c043',
        borderWidth: '2px'
      }
    }
  }
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: '8px',
  padding: '4px',
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '6px',
    margin: '0 4px',
    color: '#475569',
    textTransform: 'none',
    fontWeight: 500,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(115, 192, 67, 0.08)',
      color: '#73c043'
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(115, 192, 67, 0.12)',
      color: '#73c043',
      '&:hover': {
        backgroundColor: 'rgba(115, 192, 67, 0.16)'
      }
    }
  }
}));

const ButtonContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '4rem',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-2rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100px',
    height: '1px',
    background: 'linear-gradient(to right, transparent, #1A365D, transparent)',
    opacity: 0.2
  }
});

const LabCard = styled(Card)(({ theme }) => ({
  height: '100%',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E2E8F0',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  boxShadow: 'none',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '3px',
    background: '#004B87',
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s ease'
  },
  '&:hover': {
    backgroundColor: '#F8FAFC',
    borderColor: '#004B87',
    transform: 'translateY(-2px)',
    '&::before': {
      transform: 'scaleX(1)'
    }
  }
}));

const CardIcon = styled('img')({
  width: '48px',
  height: '48px',
  marginBottom: '1.5rem',
  transition: 'transform 0.3s ease',
  filter: 'brightness(0) saturate(100%) invert(19%) sepia(63%) saturate(1845%) hue-rotate(187deg) brightness(96%) contrast(101%)',
  '.MuiCard-root:hover &': {
    transform: 'scale(1.1)'
  }
});

const CardTitle = styled(Typography)({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#333333',
  textAlign: 'center',
  transition: 'color 0.3s ease',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  '.MuiCard-root:hover &': {
    color: '#004B87'
  }
});

const CompanyTitle = styled(Typography)({
  fontSize: '1.75rem',
  fontWeight: 600,
  color: '#004B87',
  marginBottom: '2rem',
  marginTop: '3rem',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
});

const NoResults = styled(Typography)({
  textAlign: 'center',
  color: '#475569',
  fontSize: '1.1rem',
  marginTop: '2rem'
});

const LaboratorySection = ({ title, text, link, buttonText = "En savoir plus" }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('all');
  const navigate = useNavigate();

  const handleCompanyChange = (event, newCompany) => {
    if (newCompany !== null) {
      setSelectedCompany(newCompany);
    }
  };

  const handleCardClick = (company, sector) => {
    const path = company === 'airmetrologie' ? 'air-metrologie' : company;
    navigate(`/secteurs-${path}?sector=${sector.id}`);
  };

  const getLeafSectors = (sectors) => {
    const leafSectors = [];
    
    const findLeafSectors = (sector, parentName = null) => {
      if (!sector.subSectors || sector.subSectors.length === 0) {
        leafSectors.push({
          ...sector,
          parentName
        });
      } else {
        sector.subSectors.forEach(subSector => {
          findLeafSectors(subSector, sector.name);
        });
      }
    };

    sectors.forEach(sector => findLeafSectors(sector));
    return leafSectors;
  };

  const getFilteredSectors = () => {
    let groupedSectors = {};
    
    if (selectedCompany === 'all' || selectedCompany === 'l2mi') {
      const l2miLeafSectors = getLeafSectors(sectorsData.l2mi.sectors);
      groupedSectors.l2mi = {
        name: 'L2MI',
        sectors: l2miLeafSectors.filter(sector => 
          sector.name.toLowerCase().includes(searchTerm.toLowerCase())
        ).map(sector => ({ ...sector, company: 'l2mi' }))
      };
    }
    
    if (selectedCompany === 'all' || selectedCompany === 'airmetrologie') {
      const airmetrologieLeafSectors = getLeafSectors(sectorsData.airmetrologie.sectors);
      groupedSectors.airmetrologie = {
        name: 'Air Métrologie',
        sectors: airmetrologieLeafSectors.filter(sector => 
          sector.name.toLowerCase().includes(searchTerm.toLowerCase())
        ).map(sector => ({ ...sector, company: 'airmetrologie' }))
      };
    }
    
    return groupedSectors;
  };

  const groupedSectors = getFilteredSectors();
  const hasResults = Object.values(groupedSectors).some(company => company.sectors.length > 0);

  return (
    <SectionContainer>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 4, md: 8, lg: 12 } }}>
        <HeaderSection>
          <Title>{title}</Title>
          <AnimatedUnderline align="center" sx={{ marginTop: '-1.5rem', marginBottom: '0.5rem' }} />
          <Description sx={{ marginBottom: '2rem' }}>{text}</Description>
          
          <SearchContainer>
            <SearchField
              placeholder="Rechercher un laboratoire..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#475569' }} />
                  </InputAdornment>
                ),
              }}
            />
            <StyledToggleButtonGroup
              value={selectedCompany}
              exclusive
              onChange={handleCompanyChange}
              aria-label="company filter"
            >
              <ToggleButton value="all" aria-label="all companies">
                Toutes les entreprises
              </ToggleButton>
              <ToggleButton value="l2mi" aria-label="L2MI">
                L2MI
              </ToggleButton>
              <ToggleButton value="airmetrologie" aria-label="AIRMETROLOGIE">
                AIRMETROLOGIE
              </ToggleButton>
            </StyledToggleButtonGroup>
          </SearchContainer>
        </HeaderSection>

        {!hasResults && (
          <NoResults>
            Aucun laboratoire trouvé pour votre recherche
          </NoResults>
        )}

        {Object.entries(groupedSectors).map(([key, company]) => {
          if (company.sectors.length === 0) return null;

          return (
            <Box key={key}>
              <CompanyTitle>
                {company.name}
              </CompanyTitle>
              <Grid container spacing={4} sx={{ mb: 6 }}>
                {company.sectors.map((sector, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <LabCard onClick={() => handleCardClick(sector.company, sector)}>
                      <CardContent sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        padding: '2rem',
                        '&:last-child': {
                          paddingBottom: '2rem'
                        }
                      }}>
                        <CardIcon src={sector.icon} alt={sector.name} />
                        <CardTitle>
                          {sector.parentName ? `${sector.parentName} : ${sector.name}` : sector.name}
                        </CardTitle>
                      </CardContent>
                    </LabCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          );
        })}
      </Container>
    </SectionContainer>
  );
};

export default LaboratorySection;
